import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Navigate } from "react-router-dom";
import { Plus, Search, Filter, Mail, ShieldCheck, User, MoreHorizontal, ExternalLink, Upload } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import MasterSideBar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import { getVendors } from "../../api/vendorApi";
import AddVendorForm from "../../components/operationsAssociate/AddVendorForm";
import VendorStats from "../../components/operationsAssociate/VendorStats";
import VendorProfileModal from "../../components/operationsAssociate/VendorProfileModal";
import BulkVendorUploadModal from "../../components/operationsAssociate/BulkVendorUploadModal";

export const ProtectedRoute = ({ children }) => {
    const token = localStorage.getItem("token");
    return token ? children : <Navigate to="/" />;
};

const VendorManagementPage = () => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [filters, setFilters] = useState({ search: '', status: '', category: '' });

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    
    // Sidebar logic
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const fetchVendors = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Authentication token not found.");

            const filteredParams = {};
            if (filters.search) filteredParams.search = filters.search;
            if (filters.status) filteredParams.status = filters.status;
            if (filters.category) filteredParams.category = filters.category;

            const response = await getVendors(filteredParams);
            setVendors(response.data.data);
        } catch (err) {
            setError("Synchronization failed with Vendor Registry.");
            toast.error("Failed to load vendors.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchVendors(); }, [filters]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleUpdateVendors = (updatedVendor) => {
        setVendors(prevVendors => prevVendors.map(vendor =>
            vendor._id === updatedVendor._id ? updatedVendor : vendor
        ));
    };

    return (
        <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
            <MasterSideBar 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)} 
                collapsed={sidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

                <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-7xl mx-auto space-y-8">
                        
                        {/* Header Action Bar */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                                <h1 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight uppercase">
                                    Vendor <span className="text-orange-600">Registry</span>
                                </h1>
                                <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mt-1">Status: Directory Operational</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setIsBulkModalOpen(true)}
                                    className="flex items-center gap-2 bg-white text-gray-800 border border-gray-200 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-50 transition-all shadow-sm"
                                >
                                    <Upload size={16} /> Bulk Onboard
                                </button>
                                <button
                                    onClick={() => setIsAddModalOpen(true)}
                                    className="flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-500 transition-all shadow-lg shadow-orange-100"
                                >
                                    <Plus size={16} /> Onboard New Vendor
                                </button>
                            </div>
                        </div>

                        {/* Stats Dashboard Component */}
                        <VendorStats />

                        {/* Filter Control Center */}
                        <section className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                                <div className="md:col-span-2 relative">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        placeholder="SEARCH BY NAME, EMAIL OR VENDOR CODE..."
                                        name="search"
                                        value={filters.search}
                                        onChange={handleFilterChange}
                                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-xl text-[10px] font-black focus:ring-2 focus:ring-orange-500 uppercase tracking-widest"
                                    />
                                </div>
                                <select 
                                    name="status" 
                                    onChange={handleFilterChange} 
                                    className="p-3 bg-gray-50 border-none rounded-xl text-[10px] font-black text-gray-600 uppercase tracking-widest focus:ring-2 focus:ring-orange-500"
                                >
                                    <option value="">All Statuses</option>
                                    <option value="active">Active</option>
                                    <option value="pending">Pending</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                                <select 
                                    name="category" 
                                    onChange={handleFilterChange} 
                                    className="p-3 bg-gray-50 border-none rounded-xl text-[10px] font-black text-gray-600 uppercase tracking-widest focus:ring-2 focus:ring-orange-500"
                                >
                                    <option value="">All Categories</option>
                                    <option value="supplier">Supplier</option>
                                    <option value="service_provider">Service Provider</option>
                                    <option value="contractor">Contractor</option>
                                </select>
                            </div>
                        </section>

                        {/* Data Grid Table */}
                        <section className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="bg-gray-50/50">
                                            <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Vendor Profile</th>
                                            <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Categorization</th>
                                            <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Contact Intel</th>
                                            <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                                            <th className="px-8 py-5 text-right"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {loading ? (
                                            <tr><td colSpan="5" className="px-8 py-10 text-center"><div className="flex justify-center"><span className="animate-spin h-6 w-6 border-2 rounded-full border-orange-500 border-t-transparent"></span></div></td></tr>
                                        ) : vendors.length === 0 ? (
                                            <tr><td colSpan="5" className="px-8 py-20 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest italic">No Registry Records Found</td></tr>
                                        ) : (
                                            vendors.map((vendor) => (
                                                <tr key={vendor._id} className="hover:bg-gray-50/50 transition-colors group">
                                                    <td className="px-8 py-5">
                                                        <div className="flex items-center gap-3">
                                                            <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-black text-xs uppercase">
                                                                {vendor.name.substring(0, 2)}
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-black text-gray-800 uppercase leading-none">{vendor.name}</p>
                                                                <p className="text-[9px] font-black text-orange-600 mt-1 uppercase tracking-tighter italic">{vendor.vendorCode}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-8 py-5 text-xs font-bold text-gray-500 uppercase tracking-tighter">
                                                        {vendor.category.replace('_', ' ')}
                                                    </td>
                                                    <td className="px-8 py-5">
                                                        <p className="text-xs font-bold text-gray-700 flex items-center gap-1"><Mail size={12} className="text-gray-400"/> {vendor.email}</p>
                                                    </td>
                                                    <td className="px-8 py-5">
                                                        <span className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-tighter ${
                                                            vendor.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 
                                                            vendor.status === 'inactive' ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-orange-600'
                                                        }`}>
                                                            {vendor.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-8 py-5 text-right">
                                                        <button 
                                                            onClick={() => { setSelectedVendor(vendor); setIsProfileModalOpen(true); }}
                                                            className="flex items-center gap-2 ml-auto bg-gray-900 text-white px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-orange-600 transition-all opacity-0 group-hover:opacity-100 shadow-lg shadow-gray-200"
                                                        >
                                                            Manage <ExternalLink size={12}/>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </div>
                </main>
            </div>

            {/* Modals */}
            <AnimatePresence>
                {isAddModalOpen && (
                    <AddVendorForm 
                        onClose={() => setIsAddModalOpen(false)} 
                        onVendorAdded={(newV) => { setVendors([newV, ...vendors]); setIsAddModalOpen(false); }} 
                    />
                )}

                {isBulkModalOpen && (
                    <BulkVendorUploadModal 
                        closeModal={() => setIsBulkModalOpen(false)} 
                        onVendorsUploaded={fetchVendors} 
                    />
                )}

                {isProfileModalOpen && selectedVendor && (
                    <VendorProfileModal
                        vendorId={selectedVendor._id}
                        onClose={() => { setIsProfileModalOpen(false); setSelectedVendor(null); }}
                        onVendorUpdated={handleUpdateVendors}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default VendorManagementPage;